import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { LetterFormValues, GeneratedElfLetter } from "@/types/elf-letter";
import LetterForm from "./LetterForm";
import LetterPreview from "./LetterPreview";
import LetterList from "./LetterList";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import EmailCaptureModal from "./EmailCaptureModal";
import UpsellModal from "./UpsellModal";
import { hashEmail } from "@/utils/hashEmail";
import { trackElfEvent } from "@/utils/trackElfEvent";
import { generateLetterPdfBase64 } from "@/utils/pdf/createLetterPdf";
import { isValidEmailSyntax } from "@/utils/emailValidation";
import { generateElfLetter, sendElfLetterPdf } from "@/services/d1Service";
import { MagicLetterBoxLink } from "@/components/funnel/MagicLetterBoxLink";
import FamilyMembership from "@/components/funnel/FamilyMembership";
import { useLocation } from "react-router-dom";
import LetterOccasionPicker from "./LetterOccasionPicker";
import {
  elfLetterOccasionFromSearch,
  elfLetterOccasions,
  getElfLetterOccasion,
  type ElfLetterOccasion,
  type ElfLetterOccasionId,
} from "@/data/elfLetterOccasions";
import {
  canDeliverElfLetter,
  elfLetterDraftMatches,
  emptyElfLetterDraft,
  normalizeElfLetterDraft,
  recoverElfLetterDraft,
  requiresOccasionReview,
} from "@/utils/elfLetterDraft";

const LETTERS_SESSION_KEY = "elf-letter-generator::letters-v1";
const FORM_SESSION_KEY = "elf-letter-generator::form-v1";
const ACTIVE_SESSION_KEY = "elf-letter-generator::active-letter";
const MAX_SESSION_LETTERS = 12;
const MAX_FREE_LETTERS = 3;
const USAGE_COOKIE_NAME = "elf_letter_usage";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

type EmailPreflightResult = {
  status: "valid" | "invalid" | "unavailable";
  reason?: string;
};

async function validateEmailBeforeDelivery(email: string): Promise<EmailPreflightResult> {
  try {
    const response = await fetch("/api/validate-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const data = (await response.json().catch(() => null)) as Partial<EmailPreflightResult> | null;
    if (
      data?.status === "valid" ||
      data?.status === "invalid" ||
      data?.status === "unavailable"
    ) {
      return { status: data.status, reason: data.reason };
    }
    return { status: "unavailable", reason: "malformed_response" };
  } catch {
    return { status: "unavailable", reason: "request_failed" };
  }
}

const readUsageCookie = (): Record<string, number> => {
  if (typeof document === "undefined") return {};
  const entry = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${USAGE_COOKIE_NAME}=`));
  if (!entry) return {};
  try {
    const value = decodeURIComponent(entry.split("=")[1] ?? "");
    const parsed = JSON.parse(value) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object") return {};
    const normalized: Record<string, number> = {};
    for (const [key, val] of Object.entries(parsed)) {
      const numeric = Number(val);
      if (Number.isFinite(numeric)) {
        normalized[key] = Math.max(0, Math.min(MAX_FREE_LETTERS, numeric));
      }
    }
    return normalized;
  } catch {
    return {};
  }
};

const writeUsageCookie = (map: Record<string, number>) => {
  if (typeof document === "undefined") return;
  if (!map || Object.keys(map).length === 0) {
    document.cookie = `${USAGE_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
    return;
  }
  const serialized = encodeURIComponent(JSON.stringify(map));
  document.cookie = `${USAGE_COOKIE_NAME}=${serialized}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
};

const ElfLetterGenerator: React.FC = () => {
  const { toast } = useToast();
  const location = useLocation();
  const letterTemplateRef = useRef<HTMLDivElement | null>(null);
  const sendLockRef = useRef(false);
  const generationLockRef = useRef(false);
  const emailModalOpenRef = useRef(false);
  const formValuesRef = useRef<LetterFormValues>(emptyElfLetterDraft);
  const [formValues, setFormValues] = useState<LetterFormValues>(emptyElfLetterDraft);
  const [letters, setLetters] = useState<GeneratedElfLetter[]>([]);
  const [usageMap, setUsageMap] = useState<Record<string, number>>({});
  const [activeLetterId, setActiveLetterId] = useState<string | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [selectedOccasion, setSelectedOccasion] = useState<ElfLetterOccasionId | null>(null);
  const [pendingOccasion, setPendingOccasion] = useState<ElfLetterOccasion | null>(null);
  const handledSearchRef = useRef<string | null>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [letterBeingDelivered, setLetterBeingDelivered] =
    useState<GeneratedElfLetter | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendWarning, setSendWarning] = useState<string | null>(null);
  const [remainingFreeLetters, setRemainingFreeLetters] = useState<number | null>(
    null,
  );
  const [limitReached, setLimitReached] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);

  const activeLetter = useMemo(
    () => letters.find((letter) => letter.letterId === activeLetterId),
    [letters, activeLetterId],
  );
  const activeLetterRef = useRef(activeLetter);
  activeLetterRef.current = activeLetter;
  const needsUpdate = Boolean(activeLetter && !elfLetterDraftMatches(formValues, activeLetter));
  const formLocked = !sessionLoaded || isGenerating || isSending || emailModalOpen;

  // Restore after hydration so SSR and the first client render agree. Keep v1 keys.
  useEffect(() => {
    let recoveredForm = { ...emptyElfLetterDraft };
    let recoveredLetters: GeneratedElfLetter[] = [];
    let recoveredActiveId: string | null = null;
    try {
      recoveredForm = recoverElfLetterDraft(JSON.parse(sessionStorage.getItem(FORM_SESSION_KEY) ?? "null"));
    } catch { /* A damaged draft must not prevent loading the generator. */ }
    try {
      const saved: unknown = JSON.parse(sessionStorage.getItem(LETTERS_SESSION_KEY) ?? "[]");
      if (Array.isArray(saved)) {
        recoveredLetters = saved.filter((letter): letter is GeneratedElfLetter => Boolean(
          letter && typeof letter === "object"
          && typeof letter.letterId === "string"
          && typeof letter.content === "string"
          && typeof letter.createdAt === "string"
          && !Number.isNaN(Date.parse(letter.createdAt)),
        ))
          .slice(-MAX_SESSION_LETTERS)
          .map((letter) => ({ ...letter, ...recoverElfLetterDraft(letter) }));
      }
      recoveredActiveId = sessionStorage.getItem(ACTIVE_SESSION_KEY);
    } catch { /* Session storage can be unavailable in privacy mode. */ }
    formValuesRef.current = recoveredForm;
    setFormValues(recoveredForm);
    setLetters(recoveredLetters);
    setActiveLetterId(recoveredLetters.some((letter) => letter.letterId === recoveredActiveId)
      ? recoveredActiveId
      : recoveredLetters[recoveredLetters.length - 1]?.letterId ?? null);
    setUsageMap(readUsageCookie());
    setSelectedOccasion(elfLetterOccasions.find((occasion) =>
      normalizeElfLetterDraft(recoveredForm).specialInstructions === occasion.instruction,
    )?.id ?? null);
    setSessionLoaded(true);
  }, []);

  const applyOccasion = useCallback((occasion: ElfLetterOccasion) => {
    if (sendLockRef.current || generationLockRef.current || emailModalOpenRef.current) return;
    const next = { ...formValuesRef.current, specialInstructions: occasion.instruction };
    formValuesRef.current = next;
    setFormValues(next);
    setSelectedOccasion(occasion.id);
    setPendingOccasion(null);
  }, []);

  useEffect(() => {
    if (!sessionLoaded || formLocked || handledSearchRef.current === location.search) return;
    handledSearchRef.current = location.search;
    const occasion = elfLetterOccasionFromSearch(location.search);
    if (!occasion) return;
    if (requiresOccasionReview(formValuesRef.current, undefined, true)) setPendingOccasion(occasion);
    else applyOccasion(occasion);
  }, [sessionLoaded, formLocked, location.search, applyOccasion]);

  const handleOccasionSelect = useCallback((occasion: ElfLetterOccasion) => {
    if (sendLockRef.current || generationLockRef.current || emailModalOpenRef.current) return;
    if (requiresOccasionReview(formValuesRef.current, getElfLetterOccasion(selectedOccasion)?.instruction, false)) setPendingOccasion(occasion);
    else applyOccasion(occasion);
  }, [selectedOccasion, applyOccasion]);

  useEffect(() => {
    if (!sessionLoaded) return;
    try { sessionStorage.setItem(LETTERS_SESSION_KEY, JSON.stringify(letters)); } catch { /* Keep working without storage. */ }
  }, [letters, sessionLoaded]);

  useEffect(() => {
    if (!sessionLoaded) return;
    try { sessionStorage.setItem(FORM_SESSION_KEY, JSON.stringify(formValues)); } catch { /* Keep working without storage. */ }
  }, [formValues, sessionLoaded]);

  useEffect(() => {
    if (!sessionLoaded) return;
    try {
      if (activeLetterId) {
        sessionStorage.setItem(ACTIVE_SESSION_KEY, activeLetterId);
      } else {
        sessionStorage.removeItem(ACTIVE_SESSION_KEY);
      }
    } catch { /* Keep working without storage. */ }
  }, [activeLetterId, sessionLoaded]);

  useEffect(() => {
    if (!activeLetterId && letters.length) {
      setActiveLetterId(letters[letters.length - 1].letterId);
    }
  }, [letters, activeLetterId]);

  useEffect(() => {
    if (sessionLoaded) writeUsageCookie(usageMap);
  }, [usageMap, sessionLoaded]);

  useEffect(() => {
    if (!activeLetter) {
      setLimitReached(false);
      return;
    }

    if (activeLetter.email) {
      void (async () => {
        const hash = await hashEmail(activeLetter.email as string);
        const used =
          usageMap[hash] ??
          (activeLetter.sentAt ? MAX_FREE_LETTERS : 0);
        setLimitReached(Math.max(0, MAX_FREE_LETTERS - used) <= 0);
      })();
    } else {
      setLimitReached(false);
    }
  }, [activeLetter, usageMap]);

  const handleFormChange = useCallback((values: Partial<LetterFormValues>) => {
    if (sendLockRef.current || generationLockRef.current || emailModalOpenRef.current) return;
    const next = { ...formValuesRef.current, ...values };
    formValuesRef.current = next;
    setFormValues(next);
  }, []);

  const handleLetterSelect = useCallback((letterId: string) => {
    if (sendLockRef.current || generationLockRef.current || emailModalOpenRef.current) return;
    // Browsing history never replaces the family's current form draft.
    activeLetterRef.current = letters.find((letter) => letter.letterId === letterId);
    setActiveLetterId(letterId);
  }, [letters]);

  const handleUseSavedDetails = useCallback(() => {
    if (!activeLetterRef.current || sendLockRef.current || generationLockRef.current || emailModalOpenRef.current) return;
    const next = recoverElfLetterDraft(activeLetterRef.current);
    formValuesRef.current = next;
    setFormValues(next);
    setSelectedOccasion(elfLetterOccasions.find((occasion) =>
      normalizeElfLetterDraft(next).specialInstructions === occasion.instruction,
    )?.id ?? null);
    setPendingOccasion(null);
  }, []);

  const handleClearLetters = useCallback(() => {
    if (sendLockRef.current || generationLockRef.current || emailModalOpenRef.current) return;
    activeLetterRef.current = undefined;
    setLetters([]);
    setActiveLetterId(null);
    toast({
      title: "Session cleared",
      description: "All generated letters were removed from this session.",
    });
  }, [toast]);

  const handleOpenEmailModal = useCallback(() => {
    const letter = activeLetterRef.current;
    if (!letter || generationLockRef.current || sendLockRef.current || !elfLetterDraftMatches(formValuesRef.current, letter)) return;
    emailModalOpenRef.current = true;
    setLetterBeingDelivered(letter);
    setSendError(null);
    setSendWarning(null);

    if (letter.email) {
      void (async () => {
        const hash = await hashEmail(letter.email as string);
        const used =
          usageMap[hash] ??
          (letter.sentAt ? MAX_FREE_LETTERS : 0);
        const remaining = Math.max(0, MAX_FREE_LETTERS - used);
        setRemainingFreeLetters(remaining);
        setLimitReached(remaining <= 0);
      })();
    } else {
      setRemainingFreeLetters(null);
      setLimitReached(false);
    }

    setEmailModalOpen(true);
  }, [usageMap]);

  const handleCloseEmailModal = useCallback(() => {
    if (isSending || sendLockRef.current) return;
    emailModalOpenRef.current = false;
    setEmailModalOpen(false);
    setSendError(null);
    setSendWarning(null);
    setRemainingFreeLetters(null);
    setLimitReached(false);
    setLetterBeingDelivered(null);
  }, [isSending]);

  const handleGenerate = useCallback(async () => {
    if (generationLockRef.current || sendLockRef.current || emailModalOpenRef.current || !sessionLoaded) return;
    const normalized = normalizeElfLetterDraft(formValuesRef.current);
    if (!normalized.childName) {
      setError("Please enter your child's name before generating a letter.");
      return;
    }

    generationLockRef.current = true;
    setIsGenerating(true);
    setError(null);

    const payload = {
      ...normalized,
      clientRequestId: crypto.randomUUID(),
    };

    try {
      const result = await generateElfLetter(payload);

      if (!result?.success || !result?.letterId || !result?.content) {
        throw new Error(
          result?.error || "We could not generate a letter right now.",
        );
      }

      const generated: GeneratedElfLetter = {
        letterId: result.letterId,
        clientRequestId: result.clientRequestId ?? payload.clientRequestId,
        childName: payload.childName,
        elfName: payload.elfName,
        specialInstructions: payload.specialInstructions,
        content: result.content,
        createdAt: new Date().toISOString(),
        templateType: "classic",
      };

      setLetters((prev) => {
        const next = [...prev, generated];
        if (next.length > MAX_SESSION_LETTERS) {
          next.shift();
        }
        return next;
      });
      setActiveLetterId(generated.letterId);
      activeLetterRef.current = generated;

      toast({
        title: "Letter created!",
        description: `Your elf letter for ${generated.childName} is ready to preview.`,
      });
    } catch (err) {
      console.error("Failed to generate elf letter:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong while generating the letter.",
      );
    } finally {
      generationLockRef.current = false;
      setIsGenerating(false);
    }
  }, [sessionLoaded, toast]);

  const handleEmailSubmit = useCallback(
    async (emailAddress: string) => {
      if (!letterBeingDelivered || isSending || sendLockRef.current) return;
      const assertCurrentLetter = () => {
        if (!canDeliverElfLetter(formValuesRef.current, activeLetterRef.current, letterBeingDelivered, generationLockRef.current)) {
          throw new Error("Your details changed. Close this window and update your letter before emailing it.");
        }
      };

      const normalizedEmail = emailAddress.trim().toLowerCase();
      sendLockRef.current = true;
      setIsSending(true);
      setSendError(null);
      setSendWarning(null);

      try {
        assertCurrentLetter();
        if (!isValidEmailSyntax(normalizedEmail)) {
          throw new Error("That doesn't look like a valid email.");
        }

        const preflight = await validateEmailBeforeDelivery(normalizedEmail);
        if (preflight.status === "invalid") {
          throw new Error("We couldn't verify that email domain. Please check the address and try again.");
        }
        if (preflight.status === "unavailable") {
          setSendWarning(
            "We couldn't check the email domain right now, so we'll continue and let the email provider confirm delivery.",
          );
        }

        const templateElement = letterTemplateRef.current;
        assertCurrentLetter();
        if (!templateElement) {
          throw new Error("We couldn't prepare the letter PDF. Please try again.");
        }

        const pdfBase64 = await generateLetterPdfBase64(templateElement);
        assertCurrentLetter();

        const result = await sendElfLetterPdf({
          letterId: letterBeingDelivered.letterId,
          email: normalizedEmail,
          clientRequestId: letterBeingDelivered.clientRequestId,
          pdfBase64,
          childName: letterBeingDelivered.childName,
          letterContent: letterBeingDelivered.content,
          templateType: letterBeingDelivered.templateType,
          marketingOfferVersion: "2026-10-11-offer-v1",
        });

        if (!result?.success) {
          if (typeof result?.remainingFreeLetters === "number") {
            setRemainingFreeLetters(result.remainingFreeLetters);
          }
          if (result?.limitReached) {
            setLimitReached(true);
          }
          throw new Error(
            result?.error || "We couldn’t deliver the letter yet. Please try again.",
          );
        }

        const emailHash = await hashEmail(normalizedEmail);
        const remainingFromServer =
          typeof result?.remainingFreeLetters === "number"
            ? result.remainingFreeLetters
            : null;
        const usedFromServer =
          remainingFromServer != null
            ? MAX_FREE_LETTERS - remainingFromServer
            : result?.limitReached
            ? MAX_FREE_LETTERS
            : usageMap[emailHash] ?? 0;
        const constrainedUsed = Math.max(
          0,
          Math.min(MAX_FREE_LETTERS, usedFromServer),
        );

        const sentAt = new Date().toISOString();
        setLetters((prev) =>
          prev.map((letter) =>
            letter.letterId === letterBeingDelivered.letterId
              ? { ...letter, email: normalizedEmail, sentAt }
              : letter,
          ),
        );

        setUsageMap((prev) => ({
          ...prev,
          [emailHash]: constrainedUsed,
        }));

        const remainingValue =
          remainingFromServer != null
            ? remainingFromServer
            : Math.max(0, MAX_FREE_LETTERS - constrainedUsed);

        setRemainingFreeLetters(remainingValue);
        const limitTriggered =
          Boolean(result?.limitReached) || remainingValue <= 0;
        setLimitReached(limitTriggered);
        if (limitTriggered) {
          setUpsellOpen(true);
          void trackElfEvent("elf-limit-hit");
        }

        setEmailModalOpen(false);
        emailModalOpenRef.current = false;
        setLetterBeingDelivered(null);

        void trackElfEvent("elf-email-capture");
        toast({
          title: result.alreadySent ? "Letter already sent" : "Letter sent!",
          description: result.marketingConfirmationRequired
            ? `We emailed the PDF to ${normalizedEmail}. Please use the confirmation link in that email to receive Christmas ideas and offers.`
            : result.alreadySent
            ? `We already emailed the PDF to ${normalizedEmail}. Generate a new letter if you need another delivery.`
            : `We emailed the PDF to ${normalizedEmail}.`,
        });
      } catch (err) {
        console.error("Failed to send elf letter:", err);
        setSendError(
          err instanceof Error
            ? err.message
            : "We couldn’t send the letter just yet.",
        );
      } finally {
        sendLockRef.current = false;
        setIsSending(false);
      }
    },
    [isSending, letterBeingDelivered, toast, usageMap],
  );

  return (
    <div className="grid gap-6 md:gap-8 lg:gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
      <div className="space-y-6 md:space-y-8 lg:space-y-10">
        <div className="rounded-3xl border border-muted bg-white p-5 md:p-6 lg:p-8 shadow-lg shadow-elfGreen/10">
          <div className="mb-4 md:mb-5 lg:mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              Personalize Your Elf Letter
            </h2>
            <p className="mt-1.5 md:mt-2 text-xs md:text-sm text-muted-foreground">
              Tell us about your child and we&apos;ll craft a custom message straight
              from Santa&apos;s helpers.
            </p>
          </div>

          <LetterOccasionPicker
            selected={selectedOccasion}
            pending={pendingOccasion}
            disabled={formLocked}
            onSelect={handleOccasionSelect}
            onApply={() => { if (pendingOccasion) applyOccasion(pendingOccasion); }}
            onCancel={() => setPendingOccasion(null)}
          />

          <LetterForm
            values={formValues}
            onChange={handleFormChange}
            onSubmit={handleGenerate}
            isGenerating={isGenerating}
            disabled={formLocked}
            needsUpdate={needsUpdate}
          />

          {error && (
            <Alert variant="destructive" className="mt-6 rounded-2xl text-elfRed-ink [&>svg]:text-elfRed-ink">
              <ExclamationTriangleIcon className="h-4 w-4" />
              <AlertTitle>We couldn&apos;t generate your letter</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      <div className="space-y-6 md:space-y-8 lg:space-y-10">
        <div className="rounded-3xl border border-muted bg-gradient-to-br from-white via-elfGreen-light/30 to-white p-4 md:p-5 lg:p-6 shadow-xl shadow-elfGreen/15">
          {needsUpdate && (
            <Alert className="mb-5 rounded-2xl border-elfRed/25 bg-white" role="status">
              <AlertTitle className="text-elfRed-ink">Details changed—update your letter</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>The preview below uses saved details for {activeLetter?.childName}. Your current form details are kept. Choose Update Letter to create a matching preview before emailing it.</p>
                <Button
                  type="button"
                  variant="outline"
                  disabled={formLocked}
                  onClick={handleUseSavedDetails}
                  className="min-h-11 h-auto whitespace-normal rounded-xl text-elfGreen-ink focus-visible:ring-slate-700"
                >
                  Use this letter&apos;s saved details
                </Button>
              </AlertDescription>
            </Alert>
          )}
          <LetterPreview
            ref={letterTemplateRef}
            content={activeLetter?.content}
            childName={activeLetter?.childName}
            isLoading={isGenerating}
          />
          <div className="mt-4 md:mt-5 lg:mt-6 flex flex-col items-start gap-3">
            <div className="text-xs md:text-sm text-muted-foreground">
              Enter your email to receive the printable PDF. Print the attachment
              from your inbox.
            </div>
            <Button
              onClick={handleOpenEmailModal}
              disabled={!activeLetter || needsUpdate || formLocked}
              className="w-full rounded-xl bg-elfRed-ink hover:bg-[#892638] text-white focus-visible:ring-slate-700 px-5 md:px-6 h-11 text-sm md:text-base font-semibold"
            >
              {limitReached ? "Limit Reached — Upgrade" : "Email My Printable PDF"}
            </Button>
          </div>
          {activeLetter?.sentAt && (
            <div role="status" className="mt-5 rounded-xl border border-elfGreen/30 bg-white p-4 text-sm">
              <p className="font-semibold">Your PDF has been sent. Check your inbox for the attachment.</p>
              <p className="mt-2 text-slate-600">Ready for the next adventure? Keep creating personal letters throughout December with The Magic Letter Box.</p>
              <MagicLetterBoxLink source="generator-delivery-success" className="mt-3 inline-block font-semibold text-elfGreen-ink underline underline-offset-4">Explore the Family membership</MagicLetterBoxLink>
            </div>
          )}
          {limitReached && (
            <div className="mt-3 rounded-xl bg-elfRed/10 px-4 py-2 text-xs md:text-sm font-medium text-elfRed-ink">
              You&apos;ve used all 3 free PDF deliveries for this email address.{" "}
              <MagicLetterBoxLink
                source="generator-limit-inline"
                className="underline underline-offset-4"
              >
                Upgrade to The Magic Letter Box
              </MagicLetterBoxLink>{" "}
              for unlimited letters.
            </div>
          )}
          <Separator className="my-5 md:my-6 lg:my-8 opacity-40" />
          <div className="grid gap-3 md:gap-4 rounded-2xl border border-white/70 bg-white/90 p-4 md:p-5 lg:p-6 text-xs md:text-sm shadow-inner">
            <h4 className="font-semibold text-foreground text-sm md:text-base">Session Tips</h4>
            <ul className="list-disc space-y-1.5 md:space-y-2 pl-5 text-muted-foreground">
              <li>
                Generate letters for multiple children. Selecting a saved letter changes the preview and keeps your current form details.
              </li>
              <li>
                Your letters stay saved while this tab is open. Clear the session to
                start over anytime.
              </li>
              <li>
                Ready to send? Click "Email My Printable PDF" after you&apos;re happy with your
                letter.
              </li>
            </ul>
          </div>
        </div>

        {activeLetter && <FamilyMembership source="generator-preview" title="What will your elf write next?" />}

        <div className="rounded-3xl border border-muted bg-white p-4 md:p-5 lg:p-6 shadow-xl shadow-elfGreen/5">
          <LetterList
            disabled={formLocked}
            letters={letters}
            activeLetterId={activeLetterId}
            onSelect={handleLetterSelect}
            onReset={handleClearLetters}
          />
        </div>
      </div>

      <EmailCaptureModal
        open={emailModalOpen}
        onClose={handleCloseEmailModal}
        onSubmit={handleEmailSubmit}
        letter={letterBeingDelivered}
        isSubmitting={isSending}
        error={sendError}
        warning={sendWarning}
        remainingFreeLetters={remainingFreeLetters}
        usageMap={usageMap}
        maxFreeLetters={MAX_FREE_LETTERS}
      />
      <UpsellModal
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        onUpgradeClick={() => void trackElfEvent("elf-upsell-modal")}
      />
    </div>
  );
};

export default ElfLetterGenerator;
